import React, { useState } from 'react';
import { apiClient } from '../services/api';
import { ChangePasswordForm } from '../types';
import { useAuth } from '../contexts/AuthContext';

const ZmenaHeslaPage: React.FC = () => {
  const { admin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<ChangePasswordForm>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!admin) {
      setError('Nejste přihlášeni');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Nová hesla se neshodují');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Nové heslo musí mít alespoň 6 znaků');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiClient.changePassword(admin.id, formData);
      setSuccess('Heslo bylo úspěšně změněno');
      
      // Reset form
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      setError('Chyba při změně hesla: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ChangePasswordForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h1>Změna hesla</h1>
      
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '15px', 
        borderRadius: '4px', 
        marginBottom: '20px',
        border: '1px solid #e9ecef'
      }}>
        <p><strong>Přihlášen jako:</strong> {admin?.jmeno}</p>
        <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
          Pro změnu hesla vyplňte formulář níže
        </p>
      </div>

      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '4px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={{ 
              float: 'right', 
              background: 'none', 
              border: 'none', 
              fontSize: '16px', 
              cursor: 'pointer',
              color: '#721c24'
            }}
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          borderRadius: '4px', 
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {success}
          <button 
            onClick={() => setSuccess(null)} 
            style={{ 
              float: 'right', 
              background: 'none', 
              border: 'none', 
              fontSize: '16px', 
              cursor: 'pointer',
              color: '#155724'
            }}
          >
            ×
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ 
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #ddd'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold' 
          }}>
            Aktuální heslo *
          </label>
          <input
            type="password"
            value={formData.oldPassword}
            onChange={(e) => handleInputChange('oldPassword', e.target.value)}
            required
            style={{ 
              width: '100%', 
              padding: '12px', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontSize: '14px'
            }}
            placeholder="Zadejte aktuální heslo"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold' 
          }}>
            Nové heslo *
          </label>
          <input
            type="password"
            value={formData.newPassword}
            onChange={(e) => handleInputChange('newPassword', e.target.value)}
            required
            minLength={6}
            style={{ 
              width: '100%', 
              padding: '12px', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontSize: '14px'
            }}
            placeholder="Zadejte nové heslo (min. 6 znaků)"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold' 
          }}>
            Potvrzení nového hesla *
          </label>
          <input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            required
            style={{ 
              width: '100%', 
              padding: '12px', 
              border: formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword 
                ? '1px solid #dc3545' 
                : '1px solid #ddd', 
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontSize: '14px'
            }}
            placeholder="Zadejte nové heslo znovu"
          />
          {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <p style={{ color: '#dc3545', fontSize: '12px', margin: '5px 0 0 0' }}>
              Hesla se neshodují
            </p>
          )}
        </div>

        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '10px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          fontSize: '14px',
          color: '#6c757d'
        }}>
          <strong>Požadavky na heslo:</strong>
          <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
            <li>Minimálně 6 znaků</li>
            <li>Doporučujeme použít kombinaci písmen, číslic a speciálních znaků</li>
          </ul>
        </div>

        <button
          type="submit"
          disabled={loading || !formData.oldPassword || !formData.newPassword || !formData.confirmPassword || formData.newPassword !== formData.confirmPassword}
          style={{ 
            width: '100%',
            padding: '12px 20px', 
            backgroundColor: loading ? '#6c757d' : '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {loading ? 'Měním heslo...' : 'Změnit heslo'}
        </button>
      </form>

      <div style={{ 
        marginTop: '20px', 
        fontSize: '14px', 
        color: '#6c757d',
        textAlign: 'center'
      }}>
        <p>
          ⚠️ Po změně hesla budete automaticky odhlášeni a budete se muset přihlásit znovu s novým heslem.
        </p>
      </div>
    </div>
  );
};

export default ZmenaHeslaPage;
